import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getLocalizedFaqs, useLanguage } from '../i18n';
import {
    FaChevronDown,
    FaQuestionCircle,
    FaPhone,
    FaEnvelope
} from 'react-icons/fa';

const FAQ = () => {
    const { language, t } = useLanguage();
    const faqs = getLocalizedFaqs(language);
    const [openItems, setOpenItems] = useState(new Set([1])); // First item open by default

    const toggleItem = (id) => {
        setOpenItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const scrollToContact = () => {
        const element = document.getElementById('contact');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5
            }
        }
    };

    return (
        <section id="faq" className="py-20 bg-gray-50">
            <div className="container mx-auto px-4">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                        {language === 'vi' ? <>Câu hỏi <span className="text-primary-600">Thường gặp</span></> : <span className="text-primary-600">{t('faqTitle')}</span>}
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        {t('faqDescription')}
                    </p>
                </motion.div>        <div className="grid lg:grid-cols-3 gap-12">
                    {/* FAQ Items */}
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="lg:col-span-2"
                    >
                        <div className="space-y-4">
                            {faqs.map((faq, index) => (
                                <motion.div
                                    key={faq.id}
                                    variants={itemVariants}
                                    className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden"
                                >
                                    <button
                                        onClick={() => toggleItem(faq.id)}
                                        className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                                    >
                                        <h3 className="text-lg font-semibold text-gray-900 pr-4">
                                            {faq.question}
                                        </h3>
                                        <motion.div
                                            animate={{ rotate: openItems.has(faq.id) ? 180 : 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="flex-shrink-0"
                                        >
                                            <FaChevronDown className="text-primary-600" />
                                        </motion.div>
                                    </button>

                                    <AnimatePresence>
                                        {openItems.has(faq.id) && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                                className="overflow-hidden"
                                            >
                                                <div className="px-6 pb-6">
                                                    <div className="w-full h-px bg-gray-200 mb-4"></div>
                                                    <p className="text-gray-600 leading-relaxed">
                                                        {faq.answer}
                                                    </p>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Sidebar */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="space-y-8"
                    >
                        {/* Still Have Questions Card */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg">
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                {language === 'vi' ? 'Còn câu hỏi?' : 'Still have questions?'}
                            </h3>
                            <p className="text-gray-600 mb-6">
                                {language === 'vi' ? 'Chuyên gia WordPress của chúng tôi luôn sẵn sàng giúp bạn đưa ra quyết định đúng cho dự án.' : 'Our WordPress experts are here to help you make the right decision for your project.'}
                            </p>

                            <div className="space-y-4">
                                <motion.button
                                    onClick={scrollToContact}
                                    className="w-full bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center group"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <FaEnvelope className="mr-2" />
                                    {t('sendMessage')}
                                </motion.button>

                                <motion.a
                                    href="tel:+15551234567"
                                    className="w-full border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center group"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <FaPhone className="mr-2" />
                                    {language === 'vi' ? 'Gọi ngay' : 'Call now'}
                                </motion.a>
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-8 text-white">
                            <h3 className="text-xl font-bold mb-6">{t('whyUs')}</h3>
                            <div className="space-y-4">
                                {[
                                    { number: "50+", label: language === 'vi' ? "Dự án hoàn thành" : "Projects completed" },
                                    { number: "98%", label: language === 'vi' ? "Sự hài lòng khách hàng" : "Client satisfaction" },
                                    { number: "8+", label: language === 'vi' ? "Năm kinh nghiệm" : "Years of experience" },
                                    { number: "24/7", label: language === 'vi' ? "Hỗ trợ liên tục" : "Ongoing support" }
                                ].map((stat, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: 20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1, duration: 0.5 }}
                                        className="flex items-center justify-between"
                                    >
                                        <span className="text-primary-100">{stat.label}</span>
                                        <span className="text-2xl font-bold">{stat.number}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Popular Topics */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg">
                        <h3 className="text-xl font-bold text-gray-900 mb-6">{language === 'vi' ? 'Chủ đề phổ biến' : 'Popular topics'}</h3>
                            <div className="space-y-3">
                                {[
                                    ...(language === 'vi' ? ["WordPress vs nền tảng khác", "Phát triển theme tùy chỉnh", "Thương mại điện tử với WooCommerce", "Tối ưu hóa SEO", "Bảo trì website", "Dịch vụ di chuyển"] : ["WordPress vs other platforms", "Custom theme development", "WooCommerce e-commerce", "SEO optimization", "Website maintenance", "Migration services"])
                                ].map((topic, index) => (
                                    <motion.button
                                        key={index}
                                        onClick={() => {
                                            // Find FAQ that matches this topic
                                            const matchingFaq = faqs.find(faq =>
                                                faq.question.toLowerCase().includes(topic.split(' ')[0].toLowerCase())
                                            );
                                            if (matchingFaq) {
                                                setOpenItems(new Set([matchingFaq.id]));
                                                setTimeout(() => {
                                                    document.querySelector(`[data-faq-id="${matchingFaq.id}"]`)?.scrollIntoView({
                                                        behavior: 'smooth',
                                                        block: 'center'
                                                    });
                                                }, 100);
                                            }
                                        }}
                                        className="w-full text-left text-gray-600 hover:text-primary-600 py-2 px-3 rounded-lg hover:bg-primary-50 transition-colors text-sm"
                                        whileHover={{ x: 5 }}
                                    >
                                        {topic}
                                    </motion.button>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Bottom CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="text-center mt-16"
                >
                    <div className="bg-white rounded-3xl p-12 shadow-lg border border-gray-100">
                        <h3 className="text-3xl font-bold text-gray-900 mb-4">
                            {language === 'vi' ? 'Sẵn sàng bắt đầu với dự án WordPress của bạn?' : 'Ready to start your WordPress project?'}
                        </h3>
                        <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
                            {language === 'vi' ? 'Hãy cùng thảo luận về yêu cầu của bạn và tạo ra một giải pháp WordPress tùy chỉnh giúp doanh nghiệp của bạn phát triển trực tuyến.' : 'Let’s discuss your requirements and create a custom WordPress solution that helps your business grow online.'}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <motion.button
                                onClick={scrollToContact}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center group"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                {t('freeConsultation')}
                                <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                                </svg>
                            </motion.button>
                            <motion.button
                                onClick={() => {
                                    const element = document.getElementById('pricing');
                                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                                }}
                                className="border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all inline-flex items-center justify-center"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                {language === 'vi' ? 'Xem bảng giá' : 'View pricing'}
                            </motion.button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default FAQ;
